import { BiometricAuth, BiometryError, BiometryErrorType, BiometryType } from "@aparajita/capacitor-biometric-auth";
import { Preferences } from "@capacitor/preferences";
import { isNativeApp } from "./platform";

// Biometric unlock for the native shell (Face ID / Touch ID / fingerprint).
//
// Scope (Phase 2): the biometric gates RE-ENTRY into a vendor from the launch
// screen when a session may already exist — real utility over re-typing a PIN,
// and a native API a reviewer can observe on the login path. It does not store
// or replace any credential; if the underlying customer_session has expired the
// buyer still lands on the vendor's normal PIN screen. (Phase 3's wallet makes
// this the S8 "wallet-theft floor" — mandatory lock + step-up before /enter.)

const ENABLED_KEY = "fo_biometric_enabled";
const OFFERED_KEY = "fo_biometric_offered";

export async function biometricAvailability(): Promise<{ available: boolean; label: string }> {
  if (!isNativeApp()) return { available: false, label: "" };
  try {
    const info = await BiometricAuth.checkBiometry();
    const label =
      info.biometryType === BiometryType.faceId ? "Face ID"
      : info.biometryType === BiometryType.touchId ? "Touch ID"
      : "biometric unlock";
    return { available: info.isAvailable, label };
  } catch {
    return { available: false, label: "" };
  }
}

export async function isBiometricEnabled(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { value } = await Preferences.get({ key: ENABLED_KEY });
    return value === "1";
  } catch {
    return false;
  }
}

export async function setBiometricEnabled(enabled: boolean): Promise<void> {
  if (!isNativeApp()) return;
  try {
    await Preferences.set({ key: ENABLED_KEY, value: enabled ? "1" : "0" });
  } catch {
    // best-effort
  }
}

/** Has the enable-biometric offer been shown before? (one-time prompt) */
export async function wasBiometricOffered(): Promise<boolean> {
  if (!isNativeApp()) return true;
  try {
    const { value } = await Preferences.get({ key: OFFERED_KEY });
    return value === "1";
  } catch {
    return true;
  }
}

export async function markBiometricOffered(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    await Preferences.set({ key: OFFERED_KEY, value: "1" });
  } catch {
    // best-effort
  }
}

/**
 * If biometric unlock is enabled on this device, demand it now. Returns true
 * when the user may proceed (passed, or the gate isn't enabled/available).
 * Fails CLOSED on an authentication failure, but OPEN when biometry is
 * unavailable (e.g. hardware removed) — the vendor's own PIN screen remains
 * the real credential gate underneath.
 */
export async function requireBiometricUnlock(reason: string): Promise<boolean> {
  if (!isNativeApp()) return true;
  if (!(await isBiometricEnabled())) return true;
  const { available } = await biometricAvailability();
  if (!available) return true;
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: "Cancel",
      allowDeviceCredential: true, // OS passcode fallback after biometric retries
      iosFallbackTitle: "Use Passcode",
      androidTitle: "Unlock FreshOrder",
      androidSubtitle: reason,
    });
    return true;
  } catch (error) {
    if (error instanceof BiometryError && error.code === BiometryErrorType.userCancel) {
      return false;
    }
    return false;
  }
}
